// web/src/lib/firebase.ts
import { getApp, getApps, initializeApp, type FirebaseApp } from "firebase/app";
import {
  getAuth,
  onAuthStateChanged,
  GoogleAuthProvider,
  connectAuthEmulator,
  type User,
} from "firebase/auth";
import {
  getFirestore,
  initializeFirestore,
  connectFirestoreEmulator,
  persistentLocalCache,
  persistentMultipleTabManager,
  setLogLevel as setFsLogLevel,
} from "firebase/firestore";
import {
  getStorage,
  connectStorageEmulator,
  type FirebaseStorage,
} from "firebase/storage";
import {
  getFunctions,
  connectFunctionsEmulator,
  httpsCallable,
  type HttpsCallable,
} from "firebase/functions";

/* --------------------------
   Env
-------------------------- */
const ENV: any =
  (typeof import.meta !== "undefined" && (import.meta as any).env) ||
  (typeof process !== "undefined" ? (process.env as any) : {});

const rawBucket = String(ENV.VITE_FIREBASE_STORAGE_BUCKET || "");
const normalizedBucket = rawBucket.replace(/\.firebasestorage\.app$/i, ".firebasestorage.app");

/* --------------------------
   Firebase App
-------------------------- */
const firebaseConfig = {
  apiKey: ENV.VITE_FIREBASE_API_KEY,
  authDomain: ENV.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: ENV.VITE_FIREBASE_PROJECT_ID,
  storageBucket: normalizedBucket || ENV.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: ENV.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: ENV.VITE_FIREBASE_APP_ID,
};

const app: FirebaseApp = getApps().length ? getApp() : initializeApp(firebaseConfig);

const isLocalhost =
  typeof window !== "undefined" &&
  ["localhost", "127.0.0.1"].includes(window.location.hostname);

export const USE_EMULATORS =
  isLocalhost && String(ENV.VITE_USE_EMULATORS) === "true";

/* --------------------------
   Firestore (DEFAULT DB ONLY)
-------------------------- */
// We ONLY use the default Firestore database. No databaseId specified.
export const EFFECTIVE_DB_ID = "(default)";
export const db = getFirestore(app);

if (import.meta.env.VITE_USE_FIRESTORE_EMULATOR === "1") {
  connectFirestoreEmulator(db, "localhost", 8080);
  console.log("[firebase] Firestore emulator connected");
}

/* --------------------------
   Core services
-------------------------- */
export const auth = getAuth(app);
export const storage: FirebaseStorage = getStorage(app);
export const FUNCTIONS_REGION: string =
  (ENV.VITE_FIREBASE_FUNCTIONS_REGION as string) || "us-central1";
export const functions = getFunctions(app, FUNCTIONS_REGION);

/* --------------------------
   Auth provider
-------------------------- */
export const googleProvider = new GoogleAuthProvider();
googleProvider.setCustomParameters({ prompt: "select_account" });

/* --------------------------
   Logging hygiene
-------------------------- */
try {
  setFsLogLevel(ENV.DEV ? "error" : "silent");
  if (typeof window !== "undefined") {
    const origInfo = console.info;
    console.info = (...args: any[]) => {
      const s = String(args?.[0] ?? "");
      if (s.startsWith("@firebase/firestore")) return;
      origInfo(...args);
    };
  }
} catch {/* noop */}

/* --------------------------
   Emulators (auth/storage/functions)
-------------------------- */
if (USE_EMULATORS && !(globalThis as any).__SG_EMULATORS_CONNECTED__) {
  try {
    connectAuthEmulator(auth, "http://127.0.0.1:9099", { disableWarnings: true });
    connectFirestoreEmulator(db, "127.0.0.1", 8080);
    connectStorageEmulator(storage, "127.0.0.1", 9199);
    connectFunctionsEmulator(functions, "127.0.0.1", 5001);
    (globalThis as any).__SG_EMULATORS_CONNECTED__ = true;
    console.info("[firebase] Emulators connected");
  } catch (e) {
    console.warn("[firebase] Emulator connection failed:", e);
  }
}

/* --------------------------
   Helpers
-------------------------- */
export function requireUid(): string {
  const u = auth.currentUser;
  if (!u) throw new Error("auth-required");
  return u.uid;
}

export function waitForAuth(): Promise<User | null> {
  return new Promise((resolve) => {
    const unsub = onAuthStateChanged(auth, (u) => {
      resolve(u ?? null);
      unsub();
    });
  });
}

export function callable<TReq = unknown, TRes = unknown>(
  name: string
): HttpsCallable<TReq, TRes> {
  return httpsCallable<TReq, TRes>(functions, name);
}

/* --------------------------
   DevTools handle
-------------------------- */
try {
  if (typeof window !== "undefined") {
    (window as any).__FB = {
      app,
      db,
      EFFECTIVE_DB_ID,
      PROJECT_ID: app.options.projectId,
    };
  }
} catch {/* noop */}

/* --------------------------
   One debug line
-------------------------- */
try {
  console.info("[firebase]", {
    mode: ENV.MODE,
    projectId: app.options.projectId,
    databaseId: EFFECTIVE_DB_ID,
    functionsRegion: FUNCTIONS_REGION,
    storageBucket: app.options.storageBucket,
    emulators: USE_EMULATORS,
  });
} catch {/* noop */}

/* --------------------------
   Legacy initialization (for older code)
-------------------------- */
const STORAGE_BUCKET = import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || "spellgather-beta.firebasestorage.app";
const legacyApp =
  getApps().length
    ? getApp()
    : initializeApp({
        apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
        authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
        projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
        storageBucket: STORAGE_BUCKET, // must be *.firebasestorage.app for your project
      });

export const legacyDb = getFirestore(legacyApp);
// Force storage to the exact bucket (accepts gs:// URL)
const bucketUrl = STORAGE_BUCKET.startsWith("gs://") ? STORAGE_BUCKET : `gs://${STORAGE_BUCKET}`;
export const legacyStorage = getStorage(legacyApp, bucketUrl);

// optional: quick log to verify at startup
if (import.meta.env.MODE === "development") {
  // eslint-disable-next-line no-console
  console.log("[firebase]", {
    mode: import.meta.env.MODE,
    projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
    databaseId: "(default)",
    functionsRegion: "us-central1",
    storageBucket: STORAGE_BUCKET,
  });
}

export { app, legacyApp };

